<template>
  <div class="time-attendance">
    <!-- Header -->
    <div class="page-header">
      <h2>{{ t('Time & Attendance') }}</h2>
      <div class="header-actions">
        <el-button 
          type="primary" 
          :loading="checkingInOut"
          @click="handleCheckInOut"
        >
          <el-icon><Clock /></el-icon>
          {{ canCheckIn ? t('Check In') : t('Check Out') }}
        </el-button>
      </div>
    </div>

    <!-- Current Status -->
    <div class="status-section">
      <div class="status-card">
        <div class="status-icon">
          <el-icon><Clock /></el-icon>
        </div>
        <div class="status-content">
          <h3>{{ t('Current Status') }}</h3>
          <p class="status-text">
            {{ getStatusText() }}
          </p>
          <p v-if="checkInOutStatus?.NextStatus" class="next-action">
            {{ t('Next') }}: {{ checkInOutStatus.NextStatus }}
          </p>
        </div>
      </div>
    </div>

    <!-- Today's Summary -->
    <div class="summary-section">
      <h3>{{ t('Today\'s Summary') }}</h3>
      <div class="summary-grid">
        <div class="summary-card">
          <div class="summary-icon check-in">
            <el-icon><Clock /></el-icon>
          </div>
          <div class="summary-content">
            <h4>{{ t('Check In') }}</h4>
            <p class="summary-value">{{ todayCheckIn || t('Not checked in') }}</p>
          </div>
        </div>

        <div class="summary-card">
          <div class="summary-icon check-out">
            <el-icon><Clock /></el-icon>
          </div>
          <div class="summary-content">
            <h4>{{ t('Check Out') }}</h4>
            <p class="summary-value">{{ todayCheckOut || t('Not checked out') }}</p>
          </div>
        </div>

        <div class="summary-card">
          <div class="summary-icon hours">
            <el-icon><Clock /></el-icon>
          </div>
          <div class="summary-content">
            <h4>{{ t('Work Hours') }}</h4>
            <p class="summary-value">{{ todayWorkHours }}</p>
          </div>
        </div>

        <div class="summary-card">
          <div class="summary-icon status">
            <el-icon><Document /></el-icon>
          </div>
          <div class="summary-content">
            <h4>{{ t('Status') }}</h4>
            <p class="summary-value">{{ todayStatus }}</p>
          </div>
        </div>
      </div>
    </div>

    <!-- Document Upload Dialog -->
    <DocumentUpload
      v-model="showDocumentUpload"
      :title="t('Upload Attendance Proof')"
      module="TIMEATTENDANT"
      proof-type-group="TIMEATTENDANT"
      :reference-no="attendanceReferenceNo"
      :user-id="user?.empId"
      :proof-types="attendanceProofTypes"
      :existing-documents="attendanceDocuments"
      :is-new-submission="true"
      @uploaded="handleDocumentUploaded"
      @document-deleted="handleDocumentDeleted"
    />

    <!-- Tabs -->
    <el-tabs v-model="activeTab" class="attendance-tabs">
      <el-tab-pane label="My Attendance" name="my-attendance">
        <div class="tab-content">
          <div class="filter-section">
            <div class="date-filters">
              <div class="date-field">
                <label class="date-label">{{ t('From date') }}</label>
                <el-date-picker
                  v-model="fromDate"
                  type="date"
                  :placeholder="t('From date')"
                  format="YYYY-MM-DD"
                  value-format="YYYY-MM-DD"
                  class="date-input"
                  teleported
                  :clearable="false"
                  @change="onDateFilterChange"
                />
              </div>
              <div class="date-field">
                <label class="date-label">{{ t('To date') }}</label>
                <el-date-picker
                  v-model="toDate"
                  type="date"
                  :placeholder="t('To date')"
                  format="YYYY-MM-DD"
                  value-format="YYYY-MM-DD"
                  class="date-input"
                  teleported
                  :clearable="false"
                  @change="onDateFilterChange"
                />
              </div>
              <el-button
                type="primary"
                class="date-search-btn"
                :loading="loading"
                @click="loadTimesheetHistory"
              >
                {{ t('Search') }}
              </el-button>
            </div>
          </div>

          <div class="attendance-list">
            <div
              v-for="record in (Array.isArray(timesheetHistory) ? timesheetHistory : [])"
              :key="record.DtrId"
              class="attendance-item"
            >
              <div class="attendance-header">
                <div class="attendance-date">
                  <h4>{{ formatDate(record.ShiftDate) }}</h4>
                  <p class="day-name">{{ getDayName(record.ShiftDate) }}</p>
                </div>
                <div class="attendance-status">
                  <el-tag :type="getRecordStatusType(record)">
                    {{ getRecordStatusText(record) }}
                  </el-tag>
                </div>
              </div>
              
              <div class="attendance-details">
                <div class="time-details">
                  <div class="time-item">
                    <span class="time-label">{{ t('Check In') }}:</span>
                    <span class="time-value">{{ formatTime(record.ShiftIn) || t('Not recorded') }}</span>
                  </div>
                  <div class="time-item">
                    <span class="time-label">{{ t('Check Out') }}:</span>
                    <span class="time-value">{{ formatTime(record.ShiftOut) || t('Not recorded') }}</span>
                  </div>
                </div>
                
                <div class="record-info">
                  <p class="shift-name">{{ record.ShiftName || t('Regular Shift') }}</p>
                  <p v-if="record.Remark" class="record-remark">{{ record.Remark }}</p>
                  <p class="record-type">{{ getRecordTypeText(record.RecordType) }}</p>
                  
                  <!-- Documents Section -->
                  <div v-if="record.Documents && record.Documents.length > 0" class="documents-section">
                    <h5 class="documents-title">{{ t('Supporting Documents') }}</h5>
                    <div class="documents-list">
                      <div 
                        v-for="doc in record.Documents" 
                        :key="doc.Docid"
                        class="document-item"
                      >
                        <div class="document-info">
                          <el-icon class="document-icon">
                            <Document />
                          </el-icon>
                          <span class="document-name">{{ doc.Docname }}</span>
                        </div>
                        <div class="document-actions">
                          <el-button 
                            size="small" 
                            type="primary" 
                            @click="viewDocument(doc)"
                          >
                            {{ t('View') }}
                          </el-button>
                          <el-button 
                            size="small" 
                            @click="downloadDocument(doc)"
                          >
                            {{ t('Download') }}
                          </el-button>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </el-tab-pane>

      <el-tab-pane :label="t('Pending Approvals')" name="approvals">
        <div class="tab-content">
          <div class="approval-list">
            <div
              v-for="record in (Array.isArray(timesheetForApproval) ? timesheetForApproval : [])"
              :key="record.DtrId"
              class="approval-item"
            >
              <div class="approval-header">
                <div class="employee-info">
                  <h4>{{ record.EmpName }}</h4>
                  <p class="approval-date">{{ formatDate(record.ShiftDate) }}</p>
                </div>
                <div class="approval-status">
                  <el-tag type="warning">{{ t('Pending Approval') }}</el-tag>
                </div>
              </div>
              
              <div class="approval-details">
                <div class="time-details">
                  <div class="time-item">
                    <span class="time-label">{{ t('Check In') }}:</span>
                    <span class="time-value">{{ formatTime(record.ShiftIn) || t('Not recorded') }}</span>
                  </div>
                  <div class="time-item">
                    <span class="time-label">{{ t('Check Out') }}:</span>
                    <span class="time-value">{{ formatTime(record.ShiftOut) || t('Not recorded') }}</span>
                  </div>
                </div>
                
                <div class="record-info">
                  <p class="shift-name">{{ record.ShiftName }}</p>
                  <p v-if="record.Remark" class="record-remark">{{ record.Remark }}</p>
                  <p class="record-type">{{ getRecordTypeText(record.RecordType) }}</p>
                </div>
              </div>
              
              <div class="approval-actions">
                <el-button
                  size="small"
                  type="success"
                  @click="approveTimesheet(record)"
                >
                  {{ t('Approve') }}
                </el-button>
                <el-button
                  size="small"
                  type="danger"
                  @click="rejectTimesheet(record)"
                >
                  {{ t('Reject') }}
                </el-button>
              </div>
            </div>
          </div>
        </div>
      </el-tab-pane>
    </el-tabs>

    <!-- Approval Dialog -->
    <el-dialog
      v-model="showApprovalDialog"
      :title="approvalAction === 'approve' ? t('Approve Timesheet') : t('Reject Timesheet')"
      width="90%"
      max-width="400px"
    >
      <el-form
        ref="approvalFormRef"
        :model="approvalForm"
        :rules="approvalRules"
        label-width="100px"
      >
        <el-form-item :label="t('Comments')" prop="comments">
          <el-input
            v-model="approvalForm.comments"
            type="textarea"
            :rows="3"
            :placeholder="approvalAction === 'approve' ? t('Optional comments') : t('Please provide rejection reason')"
          />
        </el-form-item>
        <el-form-item label="Attachment">
          <el-button type="success" @click="attachFile">
            <el-icon><Upload /></el-icon>
            Attach File
          </el-button>
          <div v-if="attachedFile" class="attached-file-preview" style="margin-top: 8px;">
            <el-tag type="info">
              {{ attachedFile.fileName }}
              <el-icon style="margin-left: 8px; cursor: pointer;" @click.stop="removeAttachedFile"><Delete /></el-icon>
            </el-tag>
          </div>
        </el-form-item>
      </el-form>

      <template #footer>
        <div class="dialog-footer">
          <el-button @click="showApprovalDialog = false">{{ t('Cancel') }}</el-button>
          <el-button
            :type="approvalAction === 'approve' ? 'success' : 'danger'"
            @click="submitTimesheetApproval"
            :loading="submitting"
          >
            {{ approvalAction === 'approve' ? t('Approve') : t('Reject') }}
          </el-button>
        </div>
      </template>
    </el-dialog>

    <!-- Loading -->
    <div v-if="loading" class="loading-overlay">
      <div class="loading-spinner"><el-icon class="is-loading"><Loading /></el-icon></div>
    </div>
  </div>
</template>

<script>
import { ref, reactive, computed, onMounted } from 'vue'
import { useStore } from 'vuex'
import { useRouter } from 'vue-router'
import { ElMessage } from 'element-plus'
import { 
  Calendar, 
  Clock, 
  Location, 
  Upload, 
  Download, 
  Delete, 
  View, 
  Check, 
  Close,
  Loading
} from '@element-plus/icons-vue'
import dayjs from 'dayjs'
import { useTranslation } from '@/utils/translations'
import nativeBridge from '@/services/nativeBridge'
import DocumentUpload from '@/components/DocumentUpload.vue'

export default {
  name: 'TimeAttendance',
  components: {
    Clock,
    Document,
    Loading,
    Upload,
    Delete,
    DocumentUpload
  },
  setup() {
    const store = useStore()
    const { t } = useTranslation()
    
    // Reactive data
    const activeTab = ref('my-attendance')
    const fromDate = ref('')
    const toDate = ref('')
    const showApprovalDialog = ref(false)
    const approvalAction = ref('approve')
    const selectedRecord = ref(null)
    const loading = ref(false)
    const checkingInOut = ref(false)
    const submitting = ref(false)
    const attachedFile = ref(null)
    
    // Document upload variables
    const showDocumentUpload = ref(false)
    const attendanceReferenceNo = ref('')
    const attendanceDocuments = ref([])
    const attendanceProofTypes = ref([
      { proofTypeId: 3, laDesc: 'Attendance Record', enDesc: 'Attendance Record' },
      { proofTypeId: 20, laDesc: 'Other Document', enDesc: 'Other Document' }
    ])
    
    const approvalForm = reactive({
      comments: ''
    })
    
    const approvalFormRef = ref(null)
    
    // Computed properties
    const user = computed(() => store.getters['auth/user'])
    const checkInOutStatus = computed(() => store.getters['attendance/checkInOutStatus'])
    const timesheetHistory = computed(() => {
      const history = store.getters['attendance/timesheetHistory']
      return Array.isArray(history) ? history : []
    })
    const timesheetForApproval = computed(() => {
      const approval = store.getters['attendance/timesheetForApproval']
      return Array.isArray(approval) ? approval : []
    })
    
    const canCheckIn = computed(() => store.getters['attendance/canCheckIn'])
    const canCheckOut = computed(() => store.getters['attendance/canCheckOut'])
    
    const todayCheckIn = computed(() => {
      const today = dayjs().format('YYYY-MM-DD')
      const todayRecord = timesheetHistory.value.find(record => 
        dayjs(record.ShiftDate).format('YYYY-MM-DD') === today
      )
      return todayRecord?.ShiftIn ? formatTime(todayRecord.ShiftIn) : null
    })
    
    const todayCheckOut = computed(() => {
      const today = dayjs().format('YYYY-MM-DD')
      const todayRecord = timesheetHistory.value.find(record => 
        dayjs(record.ShiftDate).format('YYYY-MM-DD') === today
      )
      return todayRecord?.ShiftOut ? formatTime(todayRecord.ShiftOut) : null
    })
    
    const todayWorkHours = computed(() => {
      const today = dayjs().format('YYYY-MM-DD')
      const todayRecord = timesheetHistory.value.find(record => 
        dayjs(record.ShiftDate).format('YYYY-MM-DD') === today
      )
      
      if (todayRecord?.ShiftIn && todayRecord?.ShiftOut) {
        const checkIn = dayjs(todayRecord.ShiftIn)
        const checkOut = dayjs(todayRecord.ShiftOut)
        const diff = checkOut.diff(checkIn, 'hour', true)
        return `${diff.toFixed(1)}h`
      }
      return '0h'
    })
    
    const todayStatus = computed(() => {
      if (todayCheckIn.value && todayCheckOut.value) {
        return 'Completed'
      } else if (todayCheckIn.value) {
        return 'Checked In'
      } else {
        return 'Not Started'
      }
    })
    
    // Form validation rules
    const approvalRules = {
      comments: [
        { 
          required: true, 
          message: t('Please provide comments'), 
          trigger: 'blur',
          validator: (rule, value, callback) => {
            if (approvalAction.value === 'reject' && !value.trim()) {
              callback(new Error(t('Rejection reason is required')))
            } else {
              callback()
            }
          }
        }
      ]
    }
    
    // Methods
    const loadAttendanceData = async () => {
      if (!user.value?.employeeId) return
      
      try {
        loading.value = true
        
        await Promise.all([
          store.dispatch('attendance/getInOutStatus'),
          store.dispatch('attendance/getTimesheetForApproval')
        ])
        
        // Set default date range to last 7 days
        const end = dayjs()
        const start = end.subtract(7, 'day')
        fromDate.value = start.format('YYYY-MM-DD')
        toDate.value = end.format('YYYY-MM-DD')
        
        await loadTimesheetHistory()
      } catch (error) {
        console.error('Failed to load attendance data:', error)
        ElMessage.error(t('Failed to load attendance data'))
      } finally {
        loading.value = false
      }
    }
    
    const onDateFilterChange = () => {
      if (fromDate.value && toDate.value && fromDate.value > toDate.value) {
        // Keep range valid when user picks out of order
        const tmp = fromDate.value
        fromDate.value = toDate.value
        toDate.value = tmp
      }
      loadTimesheetHistory()
    }

    const loadTimesheetHistory = async () => {
      if (!fromDate.value || !toDate.value) return
      
      try {
        await store.dispatch('attendance/getTimesheetHistory', {
          fromDate: fromDate.value,
          toDate: toDate.value
        })
        
        // Load documents for each attendance record
        const history = store.getters['attendance/timesheetHistory']
        if (Array.isArray(history)) {
          for (const record of history) {
            try {
              const documentsResponse = await store.dispatch('attendance/loadAttendanceDocuments', {
                empId: user.value?.empId,
                shiftDate: dayjs(record.ShiftDate).format('YYYY-MM-DD')
              })
              
              if (documentsResponse && documentsResponse.model) {
                // Add documents to the record
                record.Documents = documentsResponse.model
              }
            } catch (docError) {
              console.error('Failed to load documents for record:', record.DtrId, docError)
              // Don't show error to user, just log it
            }
          }
        }
      } catch (error) {
        console.error('Failed to load timesheet history:', error)
        ElMessage.error(t('Failed to load timesheet history'))
      }
    }
    
    const handleCheckInOut = async () => {
      try {
        checkingInOut.value = true
        
        // Get user location with native bridge or browser geolocation
        let userLatitude = 0
        let userLongitude = 0
        
        // Try native bridge first (if in Flutter app)
        if (nativeBridge.isRunningInFlutter()) {
          try {
            const nativeLocation = await nativeBridge.getCurrentLocation()
            if (nativeLocation && nativeLocation.latitude && nativeLocation.longitude) {
              userLatitude = nativeLocation.latitude
              userLongitude = nativeLocation.longitude
              console.log('Location from native bridge:', { userLatitude, userLongitude })
            } else {
              throw new Error('Native location not available')
            }
          } catch (nativeError) {
            console.log('Native location failed, falling back to browser geolocation:', nativeError)
            // Fall back to browser geolocation
            const browserLocation = await getBrowserLocation()
            userLatitude = browserLocation.latitude
            userLongitude = browserLocation.longitude
          }
        } else {
          // Use browser geolocation when not in Flutter app
          const browserLocation = await getBrowserLocation()
          userLatitude = browserLocation.latitude
          userLongitude = browserLocation.longitude
        }
        
        // Validate location first
        const locationValidation = await store.dispatch('attendance/validateLocation', {
          employeeId: user.value?.employeeId,
          latitude: userLatitude,
          longitude: userLongitude
        })
        
        if (locationValidation.didError) {
          ElMessage.error(locationValidation.errorMessage || 'Location validation failed')
          return
        }
        
        if (!locationValidation.isValid) {
          ElMessage.error(locationValidation.errorMessage || 'You are too far from the office')
          return
        }
        
        ElMessage.success(`Location validated! Distance: ${locationValidation.distance.toFixed(1)}m`)
        
        const checkInOutData = {
          empId: user.value?.employeeId,
          shiftDate: dayjs().format('YYYY-MM-DD'),
          deviceId: 'web',
          remark: canCheckIn.value ? 'Web check-in' : 'Web check-out',
          lat: userLatitude,
          lng: userLongitude,
          recordType: 3 // Mobile/web input
        }
        
        const response = await store.dispatch('attendance/checkInOut', checkInOutData)
        
        if (response && !response.didError) {
          ElMessage.success(
            canCheckIn.value ? t('Successfully checked in!') : t('Successfully checked out!')
          )
          
          // Set attendance reference number for document upload
          if (response.Model?.AttendanceReferenceNo) {
            attendanceReferenceNo.value = response.Model.AttendanceReferenceNo
            showDocumentUpload.value = true
          }
          
          // Refresh status and history
          await Promise.all([
            store.dispatch('attendance/getInOutStatus'),
            loadTimesheetHistory()
          ])
        }
      } catch (error) {
        ElMessage.error(t('Failed to check in/out. Please try again.'))
      } finally {
        checkingInOut.value = false
      }
    }

    // Helper function to get browser location
    const getBrowserLocation = async () => {
      if ('geolocation' in navigator) {
        try {
          // First check if we have permission
          const permissionStatus = await navigator.permissions.query({ name: 'geolocation' })
          
          if (permissionStatus.state === 'denied') {
            ElMessage.error('Location permission denied. Please enable location access in your browser settings.')
            throw new Error('Location permission denied')
          }
          
          if (permissionStatus.state === 'prompt') {
            ElMessage.info('Please allow location access when prompted to check in/out.')
          }
          
          const position = await new Promise((resolve, reject) => {
            navigator.geolocation.getCurrentPosition(resolve, reject, {
              enableHighAccuracy: true,
              timeout: 15000, // Increased timeout
              maximumAge: 60000
            })
          })
          
          return {
            latitude: position.coords.latitude,
            longitude: position.coords.longitude
          }
          
        } catch (locationError) {
          console.error('Location error:', locationError)
          
          // Handle specific location errors
          if (locationError.code === 1) {
            ElMessage.error('Location permission denied. Please enable location access in your browser settings.')
          } else if (locationError.code === 2) {
            ElMessage.error('Location unavailable. Please check your GPS settings.')
          } else if (locationError.code === 3) {
            ElMessage.error('Location request timed out. Please try again.')
          } else {
            ElMessage.error('Failed to get location. Please enable location services and try again.')
          }
          throw locationError
        }
      } else {
        ElMessage.error('Location services not available in this browser')
        throw new Error('Location services not available')
      }
    }
    
    const attachFile = async () => {
      if (nativeBridge.isRunningInFlutter()) {
        try {
          const file = await nativeBridge.pickFile()
          if (file) {
            attachedFile.value = file
            ElMessage.success(`File "${file.fileName}" attached.`)
          }
        } catch (error) {
          console.error('Failed to attach file:', error)
          ElMessage.error(t('Failed to attach file'))
        }
      } else {
        ElMessage.error(t('File attachment is not supported in this browser.'))
      }
    }

    const removeAttachedFile = () => {
      attachedFile.value = null
      ElMessage.info(t('Attached file removed.'))
    }
    
    // Document upload handlers
    const handleDocumentUploaded = (documents) => {
      attendanceDocuments.value = documents
      ElMessage.success(t('Attendance documents uploaded successfully'))
      showDocumentUpload.value = false
    }
    
    const handleDocumentDeleted = (documentId) => {
      attendanceDocuments.value = attendanceDocuments.value.filter(doc => doc.Docid !== documentId)
      ElMessage.success(t('Document deleted successfully'))
    }
    
    // Document viewing and downloading
    const viewDocument = async (document) => {
      try {
        // Use the existing file download utility
        const fileDownloadUtil = await import('@/utils/fileDownload.js')
        await fileDownloadUtil.default.getFileContent(document)
      } catch (error) {
        console.error('Failed to view document:', error)
        ElMessage.error(t('Failed to view document'))
      }
    }
    
    const downloadDocument = async (document) => {
      try {
        // Use the existing file download utility
        const fileDownloadUtil = await import('@/utils/fileDownload.js')
        await fileDownloadUtil.default.downloadFile(document)
      } catch (error) {
        console.error('Failed to download document:', error)
        ElMessage.error(t('Failed to download document'))
      }
    }
    
    const approveTimesheet = (record) => {
      selectedRecord.value = record
      approvalAction.value = 'approve'
      showApprovalDialog.value = true
    }
    
    const rejectTimesheet = (record) => {
      selectedRecord.value = record
      approvalAction.value = 'reject'
      showApprovalDialog.value = true
    }
    
    const submitTimesheetApproval = async () => {
      if (!approvalFormRef.value || !selectedRecord.value) return
      
      try {
        await approvalFormRef.value.validate()
        submitting.value = true
        
        const approvalData = {
          ...selectedRecord.value,
          comments: approvalForm.comments,
          attachment: attachedFile.value ? attachedFile.value.file : null
        }
        
        let response
        if (approvalAction.value === 'approve') {
          response = await store.dispatch('attendance/approveCheckInOut', approvalData)
        } else {
          response = await store.dispatch('attendance/rejectCheckInOut', approvalData)
        }
        
        if (response && !response.didError) {
          ElMessage.success(
            t(`Timesheet ${approvalAction.value === 'approve' ? 'approved' : 'rejected'} successfully`)
          )
          showApprovalDialog.value = false
          resetApprovalForm()
          await store.dispatch('attendance/getTimesheetForApproval')
        }
      } catch (error) {
        console.error('Failed to submit approval:', error)
        ElMessage.error(t('Failed to submit approval'))
      } finally {
        submitting.value = false
      }
    }
    
    const resetApprovalForm = () => {
      approvalForm.comments = ''
      attachedFile.value = null
      if (approvalFormRef.value) {
        approvalFormRef.value.resetFields()
      }
    }
    
    const getStatusText = () => {
      if (!checkInOutStatus.value) return t('Not checked in today')
      
      if (checkInOutStatus.value.NextStatus === 'CheckIn') {
        return t('Ready to check in')
      } else if (checkInOutStatus.value.NextStatus === 'CheckOut') {
        return t('Checked in - ready to check out')
      } else if (checkInOutStatus.value.NextStatus === 'Completed') {
        return t('Completed for today')
      }
      
      return t('Status unknown')
    }
    
    const getRecordStatusType = (record) => {
      if (record.ShiftIn && record.ShiftOut) return 'success'
      if (record.ShiftIn) return 'warning'
      return 'info'
    }
    
    const getRecordStatusText = (record) => {
      if (record.ShiftIn && record.ShiftOut) return t('Completed')
      if (record.ShiftIn) return t('Checked In')
      return t('Not Started')
    }
    
    const getRecordTypeText = (recordType) => {
      switch (recordType) {
        case 1: return t('Finger Scan')
        case 2: return t('Manual Entry')
        case 3: return t('Mobile App')
        default: return t('Unknown')
      }
    }
    
    const formatDate = (date) => {
      return dayjs(date).format('MMM DD, YYYY')
    }
    
    const formatTime = (time) => {
      if (!time) return null
      return dayjs(time).format('HH:mm')
    }
    
    const getDayName = (date) => {
      return dayjs(date).format('dddd')
    }
    
    onMounted(() => {
      loadAttendanceData()
    })
    
    return {
      activeTab,
      fromDate,
      toDate,
      showApprovalDialog,
      approvalAction,
      approvalForm,
      approvalFormRef,
      loading,
      checkingInOut,
      submitting,
      checkInOutStatus,
      timesheetHistory,
      timesheetForApproval,
      canCheckIn,
      canCheckOut,
      todayCheckIn,
      todayCheckOut,
      todayWorkHours,
      todayStatus,
      approvalRules,
      loadTimesheetHistory,
      onDateFilterChange,
      handleCheckInOut,
      approveTimesheet,
      rejectTimesheet,
      submitTimesheetApproval,
      getStatusText,
      getRecordStatusType,
      getRecordStatusText,
      getRecordTypeText,
      formatDate,
      formatTime,
      getDayName,
      t,
      attachedFile,
      attachFile,
      removeAttachedFile,
      showDocumentUpload,
      attendanceReferenceNo,
      attendanceDocuments,
      attendanceProofTypes,
      handleDocumentUploaded,
      handleDocumentDeleted,
      viewDocument,
      downloadDocument
    }
  }
}
</script>

<style scoped>
.time-attendance {
  padding: 20px;
  max-width: 1200px;
  margin: 0 auto;
}

.page-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 24px;
}

.page-header h2 {
  font-size: 24px;
  font-weight: 600;
  color: #333;
  margin: 0;
}

.status-section {
  margin-bottom: 24px;
}

.status-card {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
  border-radius: 16px;
  padding: 24px;
  display: flex;
  align-items: center;
  gap: 20px;
}

.status-icon {
  width: 60px;
  height: 60px;
  border-radius: 12px;
  background: rgba(255, 255, 255, 0.2);
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 28px;
}

.status-content h3 {
  font-size: 18px;
  font-weight: 600;
  margin-bottom: 8px;
}

.status-text {
  font-size: 16px;
  margin-bottom: 4px;
}

.next-action {
  font-size: 14px;
  opacity: 0.9;
}

.summary-section {
  margin-bottom: 24px;
}

.summary-section h3 {
  font-size: 18px;
  font-weight: 600;
  color: #333;
  margin-bottom: 16px;
}

.summary-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 16px;
}

.summary-card {
  background: white;
  border-radius: 12px;
  padding: 20px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  display: flex;
  align-items: center;
  gap: 16px;
}

.summary-icon {
  width: 48px;
  height: 48px;
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 24px;
  color: white;
}

.summary-icon.check-in {
  background: linear-gradient(135deg, #4CAF50, #45a049);
}

.summary-icon.check-out {
  background: linear-gradient(135deg, #FF9800, #F57C00);
}

.summary-icon.hours {
  background: linear-gradient(135deg, #2196F3, #1976D2);
}

.summary-icon.status {
  background: linear-gradient(135deg, #9C27B0, #7B1FA2);
}

.summary-content h4 {
  font-size: 14px;
  font-weight: 500;
  color: #666;
  margin-bottom: 4px;
}

.summary-value {
  font-size: 18px;
  font-weight: 700;
  color: #333;
  margin: 0;
}

.attendance-tabs {
  background: white;
  border-radius: 12px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  overflow: hidden;
}

.tab-content {
  padding: 20px;
}

.filter-section {
  margin-bottom: 16px;
}

.date-filters {
  display: flex;
  flex-wrap: wrap;
  align-items: flex-end;
  gap: 12px;
}

.date-field {
  flex: 1 1 140px;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.date-label {
  font-size: 13px;
  font-weight: 600;
  color: #555;
}

.date-input {
  width: 100% !important;
}

.date-input :deep(.el-input__wrapper),
.date-input :deep(.el-input) {
  width: 100% !important;
}

.date-search-btn {
  flex: 0 0 auto;
  min-height: 32px;
}

.attendance-list,
.approval-list {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.attendance-item,
.approval-item {
  background: #f8f9fa;
  border-radius: 8px;
  padding: 16px;
  border: 1px solid #e9ecef;
}

.attendance-header,
.approval-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 12px;
}

.attendance-date h4 {
  font-size: 16px;
  font-weight: 600;
  color: #333;
  margin-bottom: 4px;
}

.day-name {
  font-size: 14px;
  color: #666;
  margin: 0;
}

.employee-info h4 {
  font-size: 16px;
  font-weight: 600;
  color: #333;
  margin-bottom: 4px;
}

.approval-date {
  font-size: 14px;
  color: #666;
  margin: 0;
}

.attendance-details,
.approval-details {
  margin-bottom: 12px;
}

.time-details {
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin-bottom: 12px;
}

.time-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.time-label {
  font-size: 14px;
  color: #666;
  font-weight: 500;
}

.time-value {
  font-size: 14px;
  color: #333;
  font-weight: 600;
}

.record-info {
  border-top: 1px solid #e9ecef;
  padding-top: 12px;
}

.shift-name {
  font-size: 14px;
  font-weight: 600;
  color: #333;
  margin-bottom: 4px;
}

.record-remark {
  font-size: 13px;
  color: #666;
  margin-bottom: 4px;
}

.record-type {
  font-size: 12px;
  color: #999;
  margin: 0;
}

.documents-section {
  margin-top: 16px;
  padding-top: 16px;
  border-top: 1px solid #e9ecef;
}

.documents-title {
  font-size: 14px;
  font-weight: 600;
  color: #333;
  margin-bottom: 12px;
}

.documents-list {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.document-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 8px 12px;
  background: #f8f9fa;
  border-radius: 6px;
  border: 1px solid #e9ecef;
}

.document-info {
  display: flex;
  align-items: center;
  gap: 8px;
}

.document-icon {
  color: #666;
  font-size: 16px;
}

.document-name {
  font-size: 13px;
  color: #333;
  font-weight: 500;
}

.document-actions {
  display: flex;
  gap: 6px;
}

.approval-actions {
  display: flex;
  gap: 8px;
  justify-content: flex-end;
}

.loading-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(255, 255, 255, 0.8);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 9999;
}

/* Mobile responsive */
@media (max-width: 768px) {
  .time-attendance {
    padding: 12px;
    overflow-x: hidden;
  }
  
  .page-header {
    flex-direction: column;
    gap: 16px;
    align-items: stretch;
  }
  
  .status-card {
    flex-direction: column;
    text-align: center;
  }
  
  .summary-grid {
    grid-template-columns: repeat(2, 1fr);
  }

  .tab-content {
    padding: 12px;
  }

  .date-filters {
    flex-direction: column;
    align-items: stretch;
  }

  .date-field {
    flex: 1 1 auto;
    width: 100%;
  }

  .date-search-btn {
    width: 100%;
  }

  /* Single-month picker panel: fit phone width */
  :deep(.el-picker__popper),
  :deep(.el-date-picker) {
    max-width: calc(100vw - 24px) !important;
  }
  
  .attendance-header,
  .approval-header {
    flex-direction: column;
    gap: 8px;
  }
  
  .approval-actions {
    justify-content: stretch;
  }
  
  .approval-actions .el-button {
    flex: 1;
  }

  .document-item {
    flex-direction: column;
    align-items: flex-start;
    gap: 8px;
  }

  .document-actions {
    width: 100%;
  }

  .document-actions .el-button {
    flex: 1;
  }
}

@media (max-width: 480px) {
  .summary-grid {
    grid-template-columns: 1fr;
  }
}
</style>

<!-- Popper is teleported to body; keep panel on-screen on phones -->
<style>
.el-picker__popper.el-popper {
  max-width: calc(100vw - 16px) !important;
}
.el-date-picker {
  max-width: calc(100vw - 16px) !important;
}
.el-date-picker .el-picker-panel__content {
  width: auto !important;
  margin: 0 auto;
}
@media (max-width: 480px) {
  .el-date-picker {
    width: calc(100vw - 16px) !important;
  }
  .el-picker-panel__body-wrapper,
  .el-picker-panel__body {
    min-width: 0 !important;
  }
}
</style> 